import type { GeolocationPosition, GeolocationError, GeolocationErrorCode } from './types';

/**
 * Проверяет поддержку Geolocation API браузером
 */
export function isGeolocationSupported(): boolean {
  return 'geolocation' in navigator;
}

/**
 * Получает координаты с максимальной точностью
 * Использует стратегию multiple attempts для улучшения точности
 */
export function getHighAccuracyPosition(
  timeout: number = 15000
): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    if (!isGeolocationSupported()) {
      reject({
        code: 0,
        message: 'Geolocation API не поддерживается вашим браузером'
      });
      return;
    }

    // КРИТИЧЕСКИ ВАЖНЫЕ параметры для максимальной точности
    const options: PositionOptions = {
      enableHighAccuracy: true,  // ОБЯЗАТЕЛЬНО: Запрашивать GPS (не WiFi/IP)
      timeout: timeout,          // Увеличенный таймаут для ожидания GPS
      maximumAge: 0              // НЕ использовать кэш - всегда свежие координаты
    };

    console.log('🎯 Запрос GPS координат с высокой точностью...');

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const accuracy = position.coords.accuracy;
        console.log(`📍 Координаты получены. Точность: ${accuracy.toFixed(2)} метров`);

        // Логируем предупреждение если точность низкая
        if (accuracy > 100) {
          console.warn(`⚠️ Низкая точность: ${accuracy.toFixed(2)}м. Рекомендуется включить GPS и выйти на улицу.`);
        }

        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy
        });
      },
      (error) => {
        console.error(' Ошибка получения координат:', getErrorMessage(error.code));
        reject({
          code: error.code,
          message: getErrorMessage(error.code)
        });
      },
      options
    );
  });
}

/**
 * ⭐ МАКСИМАЛЬНАЯ ТОЧНОСТЬ: watchPosition с ожиданием стабилизации
 * Непрерывно отслеживает позицию и выбирает САМУЮ точную
 * Ждет пока accuracy не станет < 15м или таймаут 45 секунд
 */
export function getUltraHighAccuracyPosition(
  targetAccuracy: number = 15,
  maxWaitTime: number = 45000
): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    if (!isGeolocationSupported()) {
      reject({
        code: 0,
        message: 'Geolocation API не поддерживается вашим браузером'
      });
      return;
    }

    let watchId: number;
    let timeoutId: NodeJS.Timeout;
    let bestPosition: GeolocationPosition | null = null;
    let bestAccuracy = Infinity;
    let measurementCount = 0;

    const options: PositionOptions = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0
    };

    console.log(`🛰️ Запуск GPS трекинга (цель: < ${targetAccuracy}м, макс. ${maxWaitTime / 1000}с)...`);

    const finish = (position: GeolocationPosition) => {
      if (watchId !== undefined) {
        navigator.geolocation.clearWatch(watchId);
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      resolve(position);
    };

    watchId = navigator.geolocation.watchPosition(
      (position) => {
        measurementCount++;
        const currentAccuracy = position.coords.accuracy;

        console.log(`📡 Измерение #${measurementCount}: ${currentAccuracy.toFixed(2)}м`);

        // Сохраняем если это лучший результат
        if (currentAccuracy < bestAccuracy) {
          bestAccuracy = currentAccuracy;
          bestPosition = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: currentAccuracy
          };
          console.log(`✨ Новый лучший: ${bestAccuracy.toFixed(2)}м`);

          // Если достигли целевой точности - завершаем
          if (currentAccuracy <= targetAccuracy) {
            console.log(`🎯 Целевая точность достигнута: ${currentAccuracy.toFixed(2)}м ≤ ${targetAccuracy}м`);
            finish(bestPosition);
          }
        }
      },
      (error) => {
        // Если была хоть одна успешная попытка - возвращаем лучший результат
        if (bestPosition) {
          console.warn(`⚠️ Ошибка GPS, но есть результат: ${bestAccuracy.toFixed(2)}м`);
          finish(bestPosition);
        } else {
          if (watchId !== undefined) {
            navigator.geolocation.clearWatch(watchId);
          }
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
          reject({
            code: error.code,
            message: getErrorMessage(error.code)
          });
        }
      },
      options
    );

    // Таймаут - возвращаем лучший результат
    timeoutId = setTimeout(() => {
      if (bestPosition) {
        console.log(`⏰ Таймаут ${maxWaitTime / 1000}с. Лучший результат: ${bestAccuracy.toFixed(2)}м (${measurementCount} измерений)`);
        finish(bestPosition);
      } else {
        if (watchId !== undefined) {
          navigator.geolocation.clearWatch(watchId);
        }
        reject({
          code: 3,
          message: 'Таймаут без успешных измерений'
        });
      }
    }, maxWaitTime);
  });
}

/**
 * Улучшенная версия с несколькими попытками для достижения лучшей точности
 * Делает до 3 попыток, сохраняя самую точную
 */
export async function getHighestAccuracyPosition(
  maxAttempts: number = 3
): Promise<GeolocationPosition> {
  let bestPosition: GeolocationPosition | null = null;
  let bestAccuracy = Infinity;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      console.log(` Попытка ${attempt}/${maxAttempts}...`);

      const position = await getHighAccuracyPosition(15000);

      // Сохраняем если это лучший результат
      if (position.accuracy < bestAccuracy) {
        bestPosition = position;
        bestAccuracy = position.accuracy;
        console.log(` Новый лучший результат: ${bestAccuracy.toFixed(2)}м`);

        // Если точность отличная (< 50м), прекращаем попытки
        if (bestAccuracy < 50) {
          console.log('🎯 Отличная точность достигнута!');
          break;
        }
      }

      // Небольшая задержка между попытками
      if (attempt < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    } catch (error) {
      console.warn(`⚠️ Попытка ${attempt} не удалась:`, error);

      // Если это последняя попытка и у нас нет результата, выбрасываем ошибку
      if (attempt === maxAttempts && !bestPosition) {
        throw error;
      }
    }
  }

  if (!bestPosition) {
    throw {
      code: 2,
      message: 'Не удалось получить координаты после нескольких попыток'
    };
  }

  console.log(` Финальная точность: ${bestAccuracy.toFixed(2)} метров`);
  return bestPosition;
}

/**
 * Форматирует координаты для отображения
 */
export function formatCoordinates(lat: number, lng: number, decimals: number = 6): string {
  return `${lat.toFixed(decimals)}, ${lng.toFixed(decimals)}`;
}

/**
 * Получает понятное сообщение об ошибке геолокации
 */
export function getErrorMessage(errorCode: number): string {
  switch (errorCode) {
    case 1: // PERMISSION_DENIED
      return 'Вы отклонили запрос на доступ к местоположению';
    case 2: // POSITION_UNAVAILABLE
      return 'Не удалось определить ваше местоположение';
    case 3: // TIMEOUT
      return 'Превышено время ожидания получения координат';
    default:
      return 'Произошла неизвестная ошибка при определении местоположения';
  }
}

/**
 * Получает инструкцию по разрешению доступа для разных браузеров
 */
export function getBrowserInstructions(): string {
  const userAgent = navigator.userAgent.toLowerCase();

  if (userAgent.includes('chrome')) {
    return 'Chrome: Нажмите на иконку замка в адресной строке → Настройки сайта → Разрешить доступ к местоположению';
  } else if (userAgent.includes('firefox')) {
    return 'Firefox: Нажмите на иконку "i" в адресной строке → Разрешения → Местоположение → Разрешить';
  } else if (userAgent.includes('edge')) {
    return 'Edge: Нажмите на иконку замка в адресной строке → Разрешения для этого сайта → Местоположение → Разрешить';
  } else if (userAgent.includes('safari')) {
    return 'Safari: Настройки → Веб-сайты → Местоположение → Разрешить для этого сайта';
  }

  return 'Проверьте настройки браузера и разрешите доступ к местоположению для этого сайта';
}

/**
 * Получает название браузера
 */
export function getBrowserName(): string {
  const userAgent = navigator.userAgent.toLowerCase();

  if (userAgent.includes('chrome')) return 'Chrome';
  if (userAgent.includes('firefox')) return 'Firefox';
  if (userAgent.includes('edge')) return 'Edge';
  if (userAgent.includes('safari')) return 'Safari';
  if (userAgent.includes('opera')) return 'Opera';

  return 'Неизвестный браузер';
}

/**
 * Проверяет точность координат (считается хорошей, если < 100 метров)
 */
export function isAccuracyGood(accuracy: number): boolean {
  return accuracy < 100;
}

/**
 * Получает IP адрес пользователя (опционально)
 */
export async function getUserIP(): Promise<string | null> {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip || null;
  } catch (error) {
    console.error('Не удалось получить IP адрес:', error);
    return null;
  }
}

/**
 * Получает User Agent браузера
 */
export function getUserAgent(): string {
  return navigator.userAgent;
}

/**
 * Сохраняет флаг проверки геолокации в sessionStorage
 * Чтобы не проверять повторно в течение сессии
 */
export function setGeolocationChecked(): void {
  sessionStorage.setItem('geolocation_checked', 'true');
}

/**
 * Проверяет, была ли уже проверка геолокации в этой сессии
 */
export function wasGeolocationChecked(): boolean {
  return sessionStorage.getItem('geolocation_checked') === 'true';
}

/**
 * Очищает флаг проверки (для тестирования)
 */
export function clearGeolocationCheck(): void {
  sessionStorage.removeItem('geolocation_checked');
}
